import { useProductCustomizer } from 'pages/customization/product-customizer-hooks';
import { isJsonString } from 'utils/utils';

export const useCustomizerHandler = () => {
  const { isPartConfigured } = useProductCustomizer();

  const initializeCustomizerCanvas = ({
    customiserData,
    canvas,
    productCustomizer,
    defaultCameraAngle,
    sizeToScaling2DConverter,
    designAreaInfo,
  }) => {
    if (canvas.current) {
      try {
        let sectionsInfo = customiserData?.sections[0]?.sub_sections?.map(
          (item) => item.value
        );
        let decalsinfo = customiserData?.sections[0]?.sub_sections?.map(
          (item) => `${item.value}_decal`
        );

        productCustomizer.init(canvas.current);
        productCustomizer.runViewer();
        productCustomizer.pushInputData({
          dataType: 'RESOURCE_DATA',
          resourceType: 'SHOW_PIECE',
          resourceData: {
            resourceId: customiserData?.resource_id,
            url: customiserData?.resource_url,
            // url: 'https://cdn.maotest.com/customizer/meshes/test.glb',
            modelTexture: customiserData?.model_texture,
            sectionsInfo: sectionsInfo,
            decalsInfo: decalsinfo,
            isMain: true,
          },
        });
        productCustomizer.pushInputData({
          dataType: 'RESOURCE_DATA',
          resourceType: 'NODE_MATERIAL',
          resourceData: {
            // resourceId: customiserData?.decals_resource?.resource_id,
            resourceId: '__DECAL_MATERIAL__',
            // url: customiserData?.decals_resource?.url,
            url: 'https://cdn.maotest.com/customizer/materials/artworkMaterial.json',
          },
        });
        if (
          Array.isArray(customiserData?.patterns) &&
          customiserData?.patterns.length > 0
        ) {
          customiserData?.patterns?.forEach((pattern) => {
            productCustomizer.pushInputData({
              dataType: 'RESOURCE_DATA',
              resourceType: 'TEXTURE_PATTERN',
              resourceData: {
                resourceId: pattern?.resource_id,
                defaultScaling: pattern?.default_scaling,
                url: pattern?.url,
              },
            });
          });
        }
        if (
          Array.isArray(customiserData?.user_patterns) &&
          customiserData?.user_patterns.length > 0
        ) {
          customiserData?.user_patterns?.forEach((pattern) => {
            productCustomizer.pushInputData({
              dataType: 'RESOURCE_DATA',
              resourceType: 'TEXTURE_PATTERN',
              resourceData: {
                resourceId: pattern?.resource_id,
                defaultScaling: pattern?.default_scaling,
                url: pattern?.url,
              },
            });
          });
        }

        customiserData?.materials?.forEach((material) => {
          productCustomizer.pushInputData({
            dataType: 'RESOURCE_DATA',
            resourceType: 'NODE_MATERIAL',
            resourceData: {
              resourceId: material?.value,
              url: material?.url,
            },
          });
        });

        productCustomizer.pushInputData({
          dataType: 'RESOURCE_DATA',
          resourceType: 'ENVIRONMENT_LIGHT',
          resourceData: {
            resourceId: 'defaultEnvironment',
            url: 'https://cdn.maotest.com/customizer/textures/LightSchemes/scene.env',
          },
        });
        productCustomizer.pushInputData({
          dataType: 'SCENE_DATA',
          SCENE_DRAWER: {
            environmentTexture: 'defaultEnvironment',
            clearColor: '#F4F4F4',
            environmentIntensity: 3,
          },
        });
        productCustomizer.pushInputData({
          dataType: 'SCENE_DATA',
          MESH_SELECTOR: {
            meshSelectionEnabled: true,
          },
        });
        productCustomizer.pushInputData({
          dataType: 'SCENE_DATA',
          ARC_CAMERA: {
            movementSetup: {},
          },
        });
        if (designAreaInfo?.length > 0) {
          designAreaInfo?.forEach((areaInfo) => {
            productCustomizer.pushInputData({
              dataType: 'CONFIGURATION_DATA',
              PIKASO_GRAPHICS_EDITOR: {
                activeSection: areaInfo?.data_for_pc?.sectionId,
                zone: {
                  addRestrictedZone: {
                    newZone: {
                      ...areaInfo?.data_for_pc,
                    },
                  },
                },
              },
            });
          });
          productCustomizer.pushInputData({
            dataType: 'CONFIGURATION_DATA',
            PIKASO_GRAPHICS_EDITOR: {
              zone: {
                control: {
                  changeLockEditingStateTo: true,
                },
              },
            },
          });
        }
        productCustomizer.isReady().then(() => {
          window.dispatchEvent(new Event('resize')); // ToDo fix the modal getting smaller
          updateEachSection({
            customiserData,
            productCustomizer,
            sizeToScaling2DConverter,
            isInitialLoad: true,
          });
          setTimeout(() => {
            if (defaultCameraAngle) {
              changeCameraAngle({
                productCustomizer,
                cameraAngle: defaultCameraAngle,
              });
              goToSection({
                productCustomizer,
                sectionValue: defaultCameraAngle,
              });
            } else {
              goToSection({
                productCustomizer,
                sectionValue:
                  customiserData?.sections[0]?.sub_sections[0].value,
              });
            }
          }, 500);
        });

        // Simulating rendering delay
      } catch (error) {
        console.error('An error occurred in Loading the Customizer', error);
      }
    }
  };
  const changeCameraAngle = ({ productCustomizer, cameraAngle }) => {
    productCustomizer.pushInputData({
      dataType: 'SCENE_DATA',
      ARC_CAMERA: {
        viewPositionChange: {
          goToSide: cameraAngle,
        },
      },
    });
  };
  const highlightSection = ({ productCustomizer, sectionValue }) => {
    productCustomizer.pushInputData({
      dataType: 'SCENE_DATA',
      MESH_SELECTOR: {
        highlightSectionById: sectionValue, // Where section is id of section
      },
    });
  };
  const removeHightLight = (productCustomizer) => {
    productCustomizer.pushInputData({
      dataType: 'SCENE_DATA',
      MESH_SELECTOR: {
        highlightSectionById: 'none',
      },
    });
  };
  const goToSection = ({ productCustomizer, sectionValue }) => {
    productCustomizer.pushInputData({
      dataType: 'SCENE_DATA',
      ARC_CAMERA: {
        viewPositionChange: {
          goToSection: sectionValue, // position is the left/right/front/back/top or section id.
        },
      },
    });
  };
  const colorhandler = ({
    productCustomizer,
    showPieceId,
    sectionId,
    assignedMaterialId,
    sectionColor,
  }) => {
    productCustomizer.pushInputData({
      dataType: 'CONFIGURATION_DATA',
      SECTION_DRAWER: {
        showPieceId: showPieceId,
        sectionId: sectionId,
        assignedMaterialId: assignedMaterialId,
        sectionColor: sectionColor,
      },
    });
  };
  const logoHandler = ({
    productCustomizer,
    showPieceId,
    selectedPart,
    artwork,
  }) => {
    productCustomizer.pushInputData({
      dataType: 'CONFIGURATION_DATA',
      DECALS_DRAWER: {
        showPieceId: showPieceId,
        //   sectionId: selectedPart,
        decalId: `${selectedPart.value}_decal`,
        //   assignedMaterialId: 'leather',
        //   sectionColor: selectedColor || '#ff5733', // remove this once Simon fixes the bug
        artwork: {
          ...artwork,
          // url: url,
          // scaling : number,
          // offsetX : number,
          // offsetY : number
        },
        // text: '' // this is to add text
        // remove: true/false   // this is to remove the logo or text
      },
    });
  };
  const textHandler = ({
    productCustomizer,
    showPieceId,
    selectedPart,
    textValue,
    color,
    outlineColor,
    fontFamily,
    fontSize,
    fontWeight,
    selectedAlignmentValue,
  }) => {
    productCustomizer.pushInputData({
      dataType: 'CONFIGURATION_DATA',
      DECALS_DRAWER: {
        showPieceId: showPieceId,
        decalId: `${selectedPart.value}_decal`,
        text: {
          text: textValue,
          color: color,
          fontSize: Number(fontSize), // string | number,
          fontFamily: fontFamily, //string,
          fontWeight: fontWeight, //string,
          fontStyle: 'italic', //string,
          //   outlineWidth: number,
          // offsetX: 0, // aligned to left
          // offsetY: 0, // aligned to top
          //   offsetY: number,
          //   scaling: number,
          ...selectedAlignmentValue,
          outlineColor: outlineColor,
          outlineWidth: 5,
          scaling: 1.5,
        },
      },
    });
  };
  const patternHandler = ({
    productCustomizer,
    sectionId,
    showPieceId,
    patternId,
    remove = false,
    scaling = 4,
    backgroundColor,
    contentColor,
  }) => {
    productCustomizer.pushInputData({
      dataType: 'CONFIGURATION_DATA',
      SECTION_DRAWER: {
        sectionId: sectionId,
        showPieceId: showPieceId,
        pattern: {
          scaling: scaling,
          groupsFillConfiguration: [
            // {
            //   groupName: 'background',
            //   // color: '#005A9C',
            //   color: backgroundColor,
            // },
            {
              groupName: 'color_1',
              // color: '#2cd47b',
              color: contentColor,
            },
          ],
          id: patternId,
          remove: remove,
        },
      },
    });
  };
  const updateEachSection = ({
    customiserData,
    productCustomizer,
    sizeToScaling2DConverter,
    isInitialLoad = false,
  }) => {
    // check if anything is added to parts
    customiserData?.sections[0]?.sub_sections?.forEach((part) => {
      if (isPartConfigured(part)) {
        if (part?.pattern?.url) {
          patternHandler({
            productCustomizer: productCustomizer,
            sectionId: part?.value,
            showPieceId: customiserData?.resource_id,
            patternId: part?.pattern?.pattern_id,
            remove: false,
            scaling: part?.pattern?.default_scaling,
            backgroundColor: part?.pattern?.background_color,
            contentColor: part?.pattern?.content_color,
          });
        } else {
          patternHandler({
            productCustomizer: productCustomizer,
            sectionId: part?.value,
            showPieceId: customiserData?.resource_id,
            patternId: '',
            remove: true,
            scaling: part?.pattern?.default_scaling,
            backgroundColor: part?.pattern?.background_color,
            contentColor: part?.pattern?.content_color,
          });
        }
        if (part?.material || part?.color) {
          colorhandler({
            productCustomizer,
            showPieceId: customiserData?.resource_id,
            sectionId: part?.value,
            assignedMaterialId: part?.material?.value,
            sectionColor: part?.color,
          });
        }

        if (part?.text?.value) {
          textHandler({
            productCustomizer: productCustomizer,
            showPieceId: customiserData?.resource_id,
            selectedPart: part,
            textValue: part?.text?.value,
            fontFamily: part?.text?.font_family,
            fontSize: part?.text?.font_size,
            fontWeight: part?.text?.font_weight,
            selectedAlignmentValue: part?.text?.alignment,
            color: part?.text?.color,
            outlineColor: part?.text?.outline_color,
          });
        } else {
          textHandler({
            productCustomizer: productCustomizer,
            showPieceId: customiserData?.resource_id,
            selectedPart: part,
            textValue: '',
            fontFamily: 'Open Sans',
            fontSize: '12',
            fontWeight: 300,
            selectedAlignmentValue: {
              offsetX: -1, // aligned to left
              offsetY: 0, // aligned to top
            },
            color: '#ffff',
            outlineColor: '#ffff',
          });
        }
        if (part?.logo?.logo_url) {
          if (isInitialLoad) {
            setTimeout(() => {
              let showPiece = productCustomizer.resourceStore.resourceMap.get(
                customiserData?.resource_id
              );
              logoHandler({
                productCustomizer,
                showPieceId: customiserData?.resource_id,
                selectedPart: part,
                artwork: {
                  url: part?.logo?.logo_url,
                  ...part?.logo?.alignment,
                  scaling: customiserData?.product_height
                    ? sizeToScaling2DConverter(
                        showPiece,
                        `${part.value}_decal`,
                        part?.logo?.width,
                        part?.logo?.height,
                        1,
                        1,
                        customiserData?.product_height
                      )
                    : sizeToScaling2DConverter(
                        showPiece,
                        `${part.value}_decal`,
                        part?.logo?.width,
                        part?.logo?.height,
                        1,
                        1
                      ),
                },
              });
            }, [1500]);
          } else {
            let showPiece = productCustomizer.resourceStore.resourceMap.get(
              customiserData?.resource_id
            );
            logoHandler({
              productCustomizer,
              showPieceId: customiserData?.resource_id,
              selectedPart: part,
              artwork: {
                url: part?.logo?.logo_url,
                ...part?.logo?.alignment,
                scaling: customiserData?.product_height
                  ? sizeToScaling2DConverter(
                      showPiece,
                      `${part.value}_decal`,
                      part?.logo?.width,
                      part?.logo?.height,
                      1,
                      1,
                      customiserData?.product_height
                    )
                  : sizeToScaling2DConverter(
                      showPiece,
                      `${part.value}_decal`,
                      part?.logo?.width,
                      part?.logo?.height,
                      1,
                      1
                    ),
              },
            });
          }
        } else {
          logoHandler({
            productCustomizer: productCustomizer,
            showPieceId: customiserData?.resource_id,
            selectedPart: part,
            remove: true,
          });
        }
      }
    });
  };
  const fetchChildSectionArray = ({
    productCustomizer,
    resourceId,
    sectionValue,
  }) => {
    let getChildSections = productCustomizer?.resourceStore
      .getResource(resourceId)
      .sections.get(sectionValue)
      .getChildren(); // this will return array of child data
    let childSections = [];
    if (getChildSections?.length > 0) {
      getChildSections?.forEach((child) => {
        childSections?.push(`${child?.id}`);
      });
    }
    return childSections;
  };
  const gotToParticularLogo = ({ productCustomizer, decalId, decalParams }) => {
    productCustomizer.pushInputData({
      dataType: 'SCENE_DATA',
      ARC_CAMERA: {
        viewPositionChange: {
          goToDecal: {
            decalId: decalId,
            decalParams: decalParams,
          },
        },
      },
    });
  };
  const showAndHideDesignArea = ({ productCustomizer, show }) => {
    productCustomizer.pushInputData({
      dataType: 'CONFIGURATION_DATA',
      PIKASO_GRAPHICS_EDITOR: {
        zone: {
          control: {
            changeShowStateTo: show,
          },
        },
      },
    });
  };
  const lockCustomizerModel = ({ productCustomizer, lock = false }) => {
    productCustomizer.pushInputData({
      dataType: 'SCENE_DATA',
      ARC_CAMERA: {
        movementSetup: {
          arcMovingEnabled: !lock,
        },
      },
    });
  };
  const addLogo = ({
    productCustomizer,
    logoUrl,
    areaId = 'active',
    logoId,
  }) => {
    productCustomizer.pushInputData({
      dataType: 'CONFIGURATION_DATA',
      PIKASO_GRAPHICS_EDITOR: {
        image: {
          imageId: logoId,
          add: {
            url: logoUrl,
            fitToZone: true,
            applyToRestrictedZoneById: areaId,
          },
        },
      },
    });
  };
  const removeLogo = ({ productCustomizer, logoId }) => {
    productCustomizer.pushInputData({
      dataType: 'CONFIGURATION_DATA',
      PIKASO_GRAPHICS_EDITOR: {
        image: {
          imageId: logoId,
          control: {
            remove: true,
          },
        },
      },
    });
  };
  return {
    removeHightLight,
    colorhandler,
    textHandler,
    logoHandler,
    patternHandler,
    initializeCustomizerCanvas,
    changeCameraAngle,
    goToSection,
    highlightSection,
    updateEachSection,
    fetchChildSectionArray,
    gotToParticularLogo,
    showAndHideDesignArea,
    lockCustomizerModel,
    addLogo,
    removeLogo,
  };
};
